/**
 * 인기도 점수.
 *
 * `metricScoreSort`(정렬 스냅샷) → `metricScore` → 좋아요+댓글+조회수 합 순으로 폴백한다.
 * `useMetricScoreSort` 기본값이 **true** 라는 점에 주의 — 캠페인 등은 꺼서 부른다.
 */
import type { SocialPostInput } from 'src/utils/moments/domain/social-post-input';

/**
 * 게시물의 인기도 점수 계산
 * @param {Object} post - 게시물 객체
 * @param {number} post.metricScore - 메트릭 점수
 * @param {Object} post.metrics - 메트릭 정보
 * @returns {number} 인기도 점수
 * 
 * @example
 * getPopularityScore({ metricScore: 85, metrics: {...} }) // 85
 * getPopularityScore({ metrics: { latest: { likeCount: 10, commentCount: 5, viewCount: 100 } } }) // 115
 */
export const getPopularityScore = (
  post: SocialPostInput | null | undefined,
  options: { useMetricScoreSort?: boolean } = {},
): number => {
  const { useMetricScoreSort = true } = options;
  if (!post) return 0;
  if (post.isRepost === true) return 0;

  // 0. 커스텀 검색 정렬용 metricScoreSort가 있으면 우선 사용 (캠페인 등에서는 비활성화 가능)
  if (
    useMetricScoreSort &&
    post.metricScoreSort !== undefined &&
    post.metricScoreSort !== null
  ) {
    if (typeof post.metricScoreSort === 'number') return post.metricScoreSort;
    if (typeof post.metricScoreSort === 'object') {
      const score = Number(
        post.metricScoreSort.metricScore ??
        post.metricScoreSort.score ??
        post.metricScoreSort.value
      );
      if (Number.isFinite(score)) return score;
    }
  }

  // 1. metricScore 필드가 있으면 우선 사용
  if (post.metricScore !== undefined && post.metricScore !== null) {
    return post.metricScore;
  }

  // 2. metricScore가 없으면 기존 방식으로 계산
  const latestMetrics = post.metrics ?
    Object.values(post.metrics).pop() || {} :
    { likeCount: 0, commentCount: 0, viewCount: 0 };

  return (latestMetrics.likeCount || 0) +
    (latestMetrics.commentCount || 0) +
    (latestMetrics.viewCount || 0);
};
